import { StateCreator } from "zustand";

import {
  BookingEditModeStore,
  BulkAction,
  BulkDates,
  BulkUpdateDetails,
  Duration,
  SelectionEvent,
  SelectModeActions,
  SelectModeData,
  SelectModeSlice
} from "../booking_edit_mode_store.types";
import { INITIAL_SELECT_MODE_STATE, MAX_EVENT_HISTORY } from "../constants";
import { applyEventIncremental } from "../domain/selection.events";

export const createSelectModeSlice: StateCreator<
  BookingEditModeStore,
  [],
  [],
  { selectMode: SelectModeSlice }
> = () => ({
  selectMode: INITIAL_SELECT_MODE_STATE
});

export const createSelectModeActions = (
  set: (fn: (state: BookingEditModeStore) => void | Partial<BookingEditModeStore>) => void
): SelectModeActions => ({
  setTotalSelection: (selection: SelectModeData | null) => {
    set((state) => {
      state.selectMode.totalSelection = selection;
    });
  },

  setIsSelectAllMode: (value: boolean) => {
    set((state) => {
      state.selectMode.isSelectAllMode = value;
    });
  },

  setValuesToUpdate: (values) => {
    set((state) => {
      state.selectMode.valuesToUpdate = {
        ...state.selectMode.valuesToUpdate,
        ...values
      };
    });
  },

  setBulkAction: (action: BulkAction) => {
    set((state) => {
      state.selectMode.valuesToUpdate.bulkAction = action;
    });
  },

  changeDates: (dates: Partial<BulkDates>) => {
    set((state) => {
      state.selectMode.valuesToUpdate.dates = {
        ...state.selectMode.valuesToUpdate.dates,
        ...dates
      };
    });
  },

  toggleDetailsOption: (key: keyof BulkUpdateDetails) => {
    set((state) => {
      state.selectMode.valuesToUpdate.details[key].enabled =
        !state.selectMode.valuesToUpdate.details[key].enabled;
    });
  },

  changeDetailsOptionValue: <K extends keyof BulkUpdateDetails>(
    key: K,
    value: BulkUpdateDetails[K]["value"]
  ) => {
    set((state) => {
      state.selectMode.valuesToUpdate.details[key].value = value;
    });
  },

  changeDuration: (duration: Partial<Duration>) => {
    set((state) => {
      state.selectMode.valuesToUpdate.duration = {
        ...state.selectMode.valuesToUpdate.duration,
        ...duration
      };
    });
  },

  resetValuesToUpdate: () => {
    set((state) => {
      state.selectMode.valuesToUpdate = INITIAL_SELECT_MODE_STATE.valuesToUpdate;
    });
  },

  setMetadata: (metadata) => {
    set((state) => {
      Object.assign(state.selectMode, metadata);
    });
  },

  setIsLoading: (value: boolean) => {
    set((state) => {
      state.selectMode.isLoading = value;
    });
  },

  setIsSubmitting: (value: boolean) => {
    set((state) => {
      state.selectMode.isSubmitting = value;
    });
  },

  reset: () => {
    set((state) => {
      state.selectMode = INITIAL_SELECT_MODE_STATE;
    });
  },

  addSelectionEvent: (event: SelectionEvent) => {
    set((state) => {
      const currentRowsMap = new Map(state.selectMode.selectedRows.map((row) => [row.key, row]));

      const { selection, rowsMap } = applyEventIncremental(
        state.selectMode.selection || { filters: [] },
        currentRowsMap,
        event
      );

      const newEvents = [...state.selectMode.events, event];

      const eventsToKeep =
        newEvents.length > MAX_EVENT_HISTORY ? newEvents.slice(-MAX_EVENT_HISTORY) : newEvents;

      state.selectMode.events = eventsToKeep;
      state.selectMode.selection = selection;
      state.selectMode.selectedRows = Array.from(rowsMap.values());
    });
  }
});
